import React, {useEffect} from "react";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import styles from "./FranchisePage.module.css";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {fetchFranchiseDetails, franchiseDetailsStatusSelector, selectedFranchiseSelector} from "../franchiseSlice";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {Section} from "../../../elements/section/Section";
import {TextFieldsLine} from "../../../elements/textFieldsLine/TextFieldsLine";
import {prepareLocationNameWithId} from "../../location/locationListSlice";
import {TabChooseButton} from "../../../elements/tabChooseButton/TabChooseButton";
import {KnowledgeRecordsTab} from "../../knowledge/KnowledgeRecordsTab";
import {KnowledgeScope} from "../../../../api/generated";
import {UpdateKnowledgeRecordRequest} from "../../assistant/franchiseTypes";
import {getNewVehicleManufacturerTitle} from "../../../../utils/enumValueTitleUtil";
import {updateKnowledgeRecords} from "../../knowledge/knowledgeApi";
import {updateFranchiseRequestStatusSelector} from "../franchiseUpdateSlice";
import {WarningBadge} from "../../../elements/warningBadge/WarningBadge";
import {TextField} from "../../../elements/textField/TextField";

enum FranchisePageTabs {
    DETAILS = "DETAILS",
    KNOWLEDGE = "KNOWLEDGE"
}

export const FranchisePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const pageLocation = useLocation();
    const {assistantId, franchiseId} = useParams<{ assistantId: string; franchiseId: string }>();
    const franchise = useAppSelector(selectedFranchiseSelector);
    const status = useAppSelector(franchiseDetailsStatusSelector);
    const updateStatus = useAppSelector(updateFranchiseRequestStatusSelector);

    useEffect(() => {
        dispatch(updateSelectedMenuItem(MenuItem.FRANCHISES));
        if (franchiseId) {
            dispatch(fetchFranchiseDetails({franchisePublicId: franchiseId}));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantId, franchiseId]);

    useEffect(() => {
        if (status === RequestStatus.REJECTED) {
            navigate("/franchises");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (updateStatus === RequestStatus.RESOLVED && franchiseId) {
            dispatch(fetchFranchiseDetails({franchisePublicId: franchiseId}));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateStatus]);

    const currentTab = Object.values(FranchisePageTabs).includes(pageLocation.hash.replace("#", "") as FranchisePageTabs)
        ? pageLocation.hash.replace("#", "") as FranchisePageTabs
        : FranchisePageTabs.DETAILS;
    // Saving knowledge reloads the franchise. The knowledge tab stays mounted meanwhile, so it keeps its
    // page and the records still open for editing.
    const isReloadingKnowledge = currentTab === FranchisePageTabs.KNOWLEDGE
        && status === RequestStatus.PENDING
        && franchise?.publicId === franchiseId;

    if ((status !== RequestStatus.RESOLVED && !isReloadingKnowledge) || !franchise) {
        return <Loader/>;
    }

    const saveKnowledgeRecords = async (knowledgeRecords: UpdateKnowledgeRecordRequest[]) => {
        await updateKnowledgeRecords({
            scope: KnowledgeScope.Franchise,
            botPublicId: assistantId,
            franchisePublicId: franchise.publicId,
            knowledgeRecords
        });
        if (franchiseId) {
            dispatch(fetchFranchiseDetails({franchisePublicId: franchiseId}));
        }
    };

    const locations = franchise.publicLocationIdList || [];
    const locationLinks = locations.length
        ? <span className={styles.detailsLinkList}>
            {locations.map(location => (
                <Link
                    key={location.publicId}
                    className={styles.detailsLink}
                    to={`/locations/${location.publicId}`}
                >
                    {prepareLocationNameWithId(location.locationName || location.publicId, location.publicId)}
                </Link>
            ))}
        </span>
        : "—";
    const blankFallback = (value?: string) => value && value.trim() ? value : "—";
    const transcriptRecipients = blankFallback(
        franchise.franchiseEmailTranscriptRecipients
            ?.split(";")
            .map(recipient => recipient.trim())
            .filter(Boolean)
            .join("; ")
    );
    const newVehicleDataConfiguration = franchise.newVehicleDataConfiguration;
    const newVehicleManufacturer = newVehicleDataConfiguration?.isEnabled && newVehicleDataConfiguration.manufacturer
        ? getNewVehicleManufacturerTitle(newVehicleDataConfiguration.manufacturer, newVehicleDataConfiguration.vehicleType)
        : "—";

    return <div className={styles.wrapper}>
        <div className={styles.header}>
            <div className={styles.leftGroup}>
                <WarningBadge warnings={franchise.warnings}/>
                <span className={styles.title}>{franchise.name}</span>
            </div>
            <Button
                title={"Edit"}
                iconSrc={"/editIcon.svg"}
                state={ButtonState.SECONDARY}
                handleClick={() => dispatch(openForm({name: FormName.UPDATE_FRANCHISE, formProps: {franchise}}))}
            />
        </div>
        <div className={styles.tabContainer}>
            <TabChooseButton
                to={`#${FranchisePageTabs.DETAILS}`}
                replace={true}
                imageSrc={"/assistantIcon.svg"}
                text={"Details"}
                isActive={currentTab === FranchisePageTabs.DETAILS}
            />
            <TabChooseButton
                to={`#${FranchisePageTabs.KNOWLEDGE}`}
                replace={true}
                imageSrc={"/infoIcon.svg"}
                text={"Knowledge"}
                isActive={currentTab === FranchisePageTabs.KNOWLEDGE}
            />
        </div>
        <div className={styles.content}>
            {currentTab === FranchisePageTabs.DETAILS && <>
                <Section title={"Franchise Details"}>
                    <div className={styles.fieldsWrapper}>
                        <TextFieldsLine
                            leftFieldTitle={"Public ID"}
                            leftFieldValue={franchise.publicId}
                            rightFieldTitle={"Franchise Hash"}
                            rightFieldValue={blankFallback(franchise.franchiseHash)}
                        />
                        <TextFieldsLine
                            leftFieldTitle={"Locations"}
                            leftFieldValue={locationLinks}
                            rightFieldTitle={"URL Path Pattern"}
                            rightFieldValue={blankFallback(franchise.urlPathPattern)}
                        />
                        <TextFieldsLine
                            leftFieldTitle={"Transcript Summary"}
                            leftFieldValue={franchise.franchiseEmailTranscriptSummaryEnabled ? "Enabled" : "Disabled"}
                            rightFieldTitle={"Transcripts Filtration"}
                            rightFieldValue={franchise.franchiseEmailFiltrationEnabled ? "Enabled" : "Disabled"}
                        />
                        <TextField
                            title={"Transcript Recipient(s)"}
                            value={transcriptRecipients}
                        />
                        <TextFieldsLine
                            leftFieldTitle={"Can be in focus"}
                            leftFieldValue={franchise.focusEnabled ? "Yes" : "No"}
                            rightFieldTitle={"Shared with focused franchises"}
                            rightFieldValue={franchise.sharedWithFocusedFranchises ? "Yes" : "No"}
                        />
                        <TextFieldsLine
                            leftFieldTitle={"New Vehicle Flow"}
                            leftFieldValue={franchise.newVehicleDataConfiguration?.isEnabled ? "Enabled" : "Disabled"}
                            rightFieldTitle={"New Vehicle Manufacturer"}
                            rightFieldValue={newVehicleManufacturer}
                        />
                    </div>
                </Section>
            </>}
            {currentTab === FranchisePageTabs.KNOWLEDGE &&
                <KnowledgeRecordsTab
                    records={franchise.knowledgeRecords}
                    scope={KnowledgeScope.Franchise}
                    onSave={saveKnowledgeRecords}
                />
            }
        </div>
    </div>
}
